import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { authApi } from '../../api/endpoints.js';
import { PublicShell } from '../../app/PublicShell.js';
import {
  ActionButton,
  Alert,
  ErrorState,
  Form,
  hasNewPasswordProblem,
  NewPasswordFields,
} from '../../components/ui/index.js';

/**
 * Set a new password from a reset link.
 *
 * The password rules are checked here with the SAME shared Zod schema the
 * backend uses, so the user gets immediate feedback against exactly the rule
 * that will be applied. The check here is a convenience; the server re-runs it
 * regardless of what this page decided.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();

  // Supabase returns the recovery token in the URL fragment; a plain query
  // parameter is also accepted so the page works with either link style.
  const fragment = new URLSearchParams(
    typeof window === 'undefined' ? '' : window.location.hash.replace(/^#/, ''),
  );
  const token =
    params.get('token') ??
    fragment.get('access_token') ??
    fragment.get('token') ??
    '';

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setError(null);
    setHasSubmitted(true);

    // The same shared rules, and the same checklist, as every other place a
    // password is set (`NewPasswordFields`).
    if (hasNewPasswordProblem(password, confirmation)) return;

    setIsSubmitting(true);
    try {
      await authApi.confirmPasswordReset(token, password);
      setIsDone(true);
    } catch (caught) {
      setError(caught);
    } finally {
      setIsSubmitting(false);
    }
  };

  const backToSignIn = (
    <Link
      to="/login"
      className="rounded-sm text-center text-body text-primary-800 hover:underline"
    >
      {t('auth.backToSignIn')}
    </Link>
  );

  return (
    <PublicShell
      title={t('auth.resetTitle')}
      subtitle={t('auth.resetSubtitle')}
    >
      {isDone ? (
        <div className="flex flex-col gap-stack">
          <Alert tone="success" title={t('auth.resetDone')} />
          {backToSignIn}
        </div>
      ) : !token ? (
        <div className="flex flex-col gap-stack">
          <Alert tone="warning" title={t('auth.missingResetToken')} />
          <Link
            to="/forgot-password"
            className="rounded-sm text-center text-body text-primary-800 hover:underline"
          >
            {t('auth.forgotTitle')}
          </Link>
        </div>
      ) : (
        <Form onSubmit={handleSubmit} isSubmitting={isSubmitting}>
          <NewPasswordFields
            password={password}
            confirmation={confirmation}
            onPasswordChange={setPassword}
            onConfirmationChange={setConfirmation}
            showProblems={hasSubmitted}
          />

          {error ? <ErrorState error={error} /> : null}

          <ActionButton
            priority="primary"
            type="submit"
            isLoading={isSubmitting}
            isDisabled={!password || !confirmation}
            fullWidth
          >
            {t('auth.resetSubmit')}
          </ActionButton>
        </Form>
      )}
    </PublicShell>
  );
}
